<template>
    <v-data-table
        :headers="headers"
        :items="listItems"
        :expanded.sync="expandedItems"
        @click:row="expandItemByClick($event)"
        show-expand
        :loading="isLoading"
        hide-default-footer
        :header-props="{ sortIcon: '' }"
        :no-data-text="$t('lang_GENERAL_MESSAGE_NO_RECORDS_FOUND')"
        :no-results-text="$t('lang_GENERAL_MESSAGE_NO_RECORDS_FOUND')"
        :loading-text="$t('lang_GENERAL_LOADING_DATATABLE')"
        class="noMinWidth"
    >
    <!-- Top Slot -->
        <template v-slot:top>
            <v-row class="pb-3">
                <v-col cols="2">
                    <DropdownFilter 
                        context="ProfitAndLossFilterCostGroup"
                        label="lang_LABEL_RELEASES_CAPTION_COST_GROUP"
                        :items="costGroupItems"
                        :alvqa="alvqa + '-dropdown-filter-cost-group'"
                        :defaultValueNull="true"
                    />
                </v-col>
                <v-col 
                    cols="3"
                    class="pt-8"
                    :alvqa="alvqa + '-sum'"
                >
                    {{ $t('lang_GENERIC_CAPTION_SUM') }}:
                    {{ $n(getSumOfDisplayedRows, 'number') }} {{ $store.getters.getSelectedCurrency }}
                </v-col>
            </v-row>
        </template>

    <!-- Headers -->
        <template v-for="(header, i) in headers" v-slot:[`header.${header.value}`]>
            <span :alvqa="header.alvqa" :key="i">{{ header.text }}</span>
        </template>
    
    <!-- Items -->
        <!-- Expand Icon -->
        <template v-slot:[`item.data-table-expand`]="{ isExpanded }">
            <v-icon 
                v-if="isExpanded"
                small 
                class="ma-0 pa-0"
                color="buttonStandard"
                :alvqa="alvqa + '-list-row-expand-icon-open'"
            >fa-arrow-alt-circle-down</v-icon>
            <v-icon 
                v-else
                small 
                class="ma-0 pa-0"
                color="buttonStandard"
                :alvqa="alvqa + '-list-row-expand-icon-close'"
            >fa-arrow-alt-circle-right</v-icon>
        </template>
        <template v-slot:[`item.costGroup`]="{ item }">
            <div :alvqa="alvqa + '-list-row-text-cost-group-'+ item.costGroup">
                {{ getCostGroup(item.costGroup) }}
            </div>
        </template>
        <template v-slot:[`item.budgetPercent`]="{ item }">
            <div 
                class="text-right"
                :alvqa="alvqa + '-list-row-text-budget-percent-'+ item.costGroup"
            >
                {{ $alvPlugin.getNumberLocalization(item.budgetPercent, 'number') }}
            </div>
        </template>
        <template v-slot:[`item.difference`]="{ item }">
            <div 
                class="text-right"
                :alvqa="alvqa + '-list-row-text-difference-'+ item.costGroup"
            >
                {{ $alvPlugin.getNumberLocalization(item.difference, 'number') }}
            </div>
        </template>
        <template v-slot:[`item.budget`]="{ item }">
            <div 
                class="text-right"
                :alvqa="alvqa + '-list-row-text-budget-'+ item.costGroup"
            >
                {{ $alvPlugin.getNumberLocalization(item.budget, 'number') }}
            </div>
        </template>
        <template v-slot:[`item.costsResult`]="{ item }">
            <div 
                class="text-right"
                :alvqa="alvqa + '-list-row-text-result-'+ item.costGroup"
            >
                {{ $alvPlugin.getNumberLocalization(item.costsResult, 'number') }}
            </div>
        </template>

        <!-- EXPANDED -->
        <template v-slot:expanded-item="{ item }">
            <td
                colspan="1"
                style="background-color: var(--v-expandPanelBackground-base);"
            ></td>
            <td 
                class="pl-0"
                :colspan="headers.length - 1"
                style="background-color: var(--v-expandPanelBackground-base);"
            >
                <v-container fluid class="pl-0 pr-0">
                    <v-row>
                        <v-col class="pr-0">
                            <v-data-table         
                                :headers="subheaders"
                                hide-default-footer
                                :items="item.rows"
                                :header-props="{ sortIcon: '' }"
                                :no-data-text="$t('lang_GENERAL_MESSAGE_NO_RECORDS_FOUND')"
                                class="noMinWidth"
                            >
                            <!-- Headers -->
                                <template v-for="(header, i) in subheaders" v-slot:[`header.${header.value}`]>
                                    <span :alvqa="header.alvqa + '-' + item.costGroup" :key="i">{{ header.text }}</span>
                                </template>

                            <!-- Items -->
                                <template v-slot:[`item.date`]="{ item, index }">
                                    <div :alvqa="alvqa + '-list-row-' + index + '-text-date-' + item.costgroup">
                                        {{ item.date !== null ? $d(new Date(item.date), 'alvDateFormat') : '' }}
                                    </div>
                                </template>
                                <template v-slot:[`item.description`]="{ item, index }">
                                    <div class="ellipsis">
                                        <BaseTooltip 
                                            :simpleText="item.description"
                                            :alvqa="alvqa + '-list-row-' + index + '-text-description-' +  item.costgroup"
                                        />
                                    </div>
                                </template>
                                <template v-slot:[`item.costs`]="{ item, index }">
                                    <div 
                                        class="text-right"
                                        :alvqa="alvqa + '-list-row-' + index + '-text-costs-' +  item.costgroup"
                                    >
                                        {{ $alvPlugin.getNumberLocalization(item.costs, 'number') }}
                                    </div>
                                </template>
                            </v-data-table>
                        </v-col>
                    </v-row>
                </v-container>
            </td>
        </template>
    </v-data-table>
</template>

<script>
import { DropdownGenericCostGroupTypes } from '@/assets/dropdowns/alvDropdowns.js'

import DropdownFilter from '@/components/_filters/DropdownFilter'
import BaseTooltip from '@/components/_base/BaseTooltip'

export default {
    name: "ProfitAndLossCostsDataTableReadOnly.vue",
    components: {
        DropdownFilter,
        BaseTooltip
    },
    props: {
        releaseAlvId: {
            type: String
        }
    },
    data() {
        return {
            alvqa: 'label-releases-list-row-' + this.releaseAlvId + '-read-only-panel-profit-and-loss-costs',
            expandedItems: []
        }
    },
    computed: {
        headers() {
            return [
                {
					text: '', 
					value: 'data-table-expand',
					align: 'center', 
                    sortable: false,
					width: "48px"
				},
                {
                    text: this.$t("lang_LABEL_RELEASES_PANEL_CAPTION_COSTS"), 
                    align: "left", 
                    value: "costGroup",
                    class: 'text-no-wrap',
                    alvqa: this.alvqa + '-header-costs',
                    sortable: false,
                    filter: this.filterCostGroup,
                },
                {
                    text: this.$t('lang_GENERIC_CAPTION_BUDGET') + " %", 
                    align: "right", 
                    value: "budgetPercent",
                    class: 'text-no-wrap',
                    sortable: false,
                    alvqa: this.alvqa + '-header-budget-percent'
                },
                {
                    text: this.$t('lang_GENERIC_CAPTION_DIFF') + ' ' + this.$store.getters.getSelectedCurrency, 
                    align: "right", 
                    value: "difference",
                    class: 'text-no-wrap',
                    sortable: false,
                    alvqa: this.alvqa + '-header-difference'
                },
                {
                    text: this.$t('lang_GENERIC_CAPTION_BUDGET') + ' ' + this.$store.getters.getSelectedCurrency, 
                    align: "right", 
                    value: "budget",
                    class: 'text-no-wrap',
                    sortable: false,
                    alvqa: this.alvqa + '-header-budget'
                },
                {
                    text: this.$t('lang_GENERIC_CAPTION_ACTUAL') + ' ' + this.$store.getters.getSelectedCurrency, 
                    align: "right", 
                    value: "costsResult",
                    class: 'text-no-wrap',
                    sortable: false,
                    alvqa: this.alvqa + '-header-result'
                }
            ]
        },
        subheaders() {
            return [
                {
                    text: this.$t('lang_GENERIC_CAPTION_DATE'), 
                    value: 'date', 
                    align: 'left', 
                    sortable: false,
                    class: 'text-no-wrap',
                    width: '250px',
                    alvqa: this.alvqa + '-expand-header-date'
                },
                {
                    text: this.$t("lang_LABEL_RELEASES_CAPTION_DESCRIPTION"), 
                    align: "left", 
                    value: "description",
                    class: 'text-no-wrap',
                    sortable: false,
                    alvqa: this.alvqa + '-expand-header-description'
                },
                {
                    text: this.$t('lang_GENERIC_CAPTION_ACTUAL') + ' ' + this.$store.getters.getSelectedCurrency, 
                    align: "right", 
                    value: "costs",
                    class: 'text-no-wrap',
                    sortable: false,
                    alvqa: this.alvqa + '-expand-header-costs'
                }
            ]
        },

        listItems() { 
            let costgroups = this.$store.getters.getReleasesProfitAndLoss.costs
            if(costgroups !== null && costgroups !== undefined){
                costgroups.forEach((costgroup, index) => {
                    costgroup.rows.forEach(row => {
                        row['costgroup'] = costgroup.costGroup
                    });
                });
            }
            return costgroups 
        },
        isLoading() { return this.$store.getters.getProfitAndLossIsLoading },
        costGroupItems() {
            let costGroup = JSON.parse(JSON.stringify(DropdownGenericCostGroupTypes))
            if(costGroup !== null && costGroup !== undefined){
                costGroup.forEach(element => {
                    element.text = this.$t(element.text)
                });
            }
            costGroup.sort((a,b) => (a.text > b.text) ? 1 : (b.text > a.text) ? -1 : 0)
            return costGroup
        },
        getSumOfDisplayedRows() {
            let selected = this.$store.getters.getSelectedProfitAndLossFilterCostGroup
            let sum = 0

            // If api call for the panel is still pending we return default value 0
            if(this.listItems === undefined) return sum
            // if no filter is set, just sum up the "IST-amount" of all rows.
            if(!selected) sum = this.listItems.reduce((prev, cur) => prev + this.$alvPlugin.stringToNumber(cur.costsResult), 0)
            else { // if the filter is set
                this.listItems.forEach(row => {
                    if(row.costGroup === selected) sum += this.$alvPlugin.stringToNumber(row.costsResult)
                })
            }
            
            return sum
        }
    },
    methods: {
        getCostGroup(costGroupvalue) {

            let costGroupsTypes = DropdownGenericCostGroupTypes

            if(costGroupsTypes === null || costGroupsTypes === undefined) {
                return ''
            } else {
                let match = costGroupsTypes.find(type => {
                return type.value === costGroupvalue
                })

                if(match !== undefined){
                    return this.$t(match.text)
                }
            }
        },
        // Expand behaviour when clicking on a row. 
        async expandItemByClick(item) {            
            if(this.expandedItems.includes(item)) { // close item.
                let indexOfItem = this.expandedItems.indexOf(item)
                this.expandedItems.splice(indexOfItem, 1) 
            } else { // open item             
                this.expandedItems.push(item) 
            }
        },
        filterCostGroup(value) {
            // https://www.codeply.com/p/krbUh6q9yE
            let selected = this.$store.getters.getSelectedProfitAndLossFilterCostGroup

            if (!selected) return true // return "true" for all rows in data table = show all rows (no filter selected)
            else return value === selected // check if the value of the row is matching the filter and show row in data table
        }
    }
}
</script>

<style>
    .noMinWidth table {
        min-width: 0px !important;
        table-layout: fixed;
    }
</style>